import React from "react";
import { tv, type VariantProps } from "tailwind-variants";

const buttonVariants = tv({
  base: [
    "rounded-2xl",
    "font-bold",
    "cursor-pointer",
    "transition-colors",
    "aria-disabled:cursor-not-allowed",
    "disabled:text-gray-500",
    "disabled:cursor-not-allowed",
    "h-[48px]",
    "text-[18px]"
  ],
  variants: {
    appearance: {
      primary: [
        "bg-brand-magenta-500",
        "text-white",
        "hover:bg-brand-magenta-600",
        "active:bg-brand-magenta-700",
        "disabled:bg-gray-300"
      ],
      secondary: [
        "bg-white",
        "text-brand-cyan-600",
        "border",
        "border-brand-cyan-600",
        "hover:border-brand-cyan-500",
        "hover:text-brand-cyan-500",
        "active:text-brand-cyan-700",
        "active:border-brand-cyan-700",
        "aria-disabled:bg-gray-300",
        "aria-disabled:text-gray-500",
        "aria-disabled:border-gray-300",
        "disabled:bg-gray-300",
        "disabled:text-gray-500",
        "disabled:border-gray-300"
      ],
      tertiary: [
        "text-brand-cyan-600",
        "bg-transparent",
        "border-none",
        "hover:text-brand-cyan-500",
        "active:text-brand-cyan-700",
        "aria-disabled:text-gray-500",
        "disabled:text-gray-500"
      ]
    },
    widthVariant: {
      full: "w-full",
      fit: "w-fit"
    },
    center: {
      true: ["flex", "justify-center", "items-center", "gap-2", "text-center"],
      false: []
    },
    ejectPadding: {
      true: [],
      false: ["py-3", "px-6"]
    }
  },
  defaultVariants: {
    appearance: "primary",
    widthVariant: "fit",
    center: true,
    ejectPadding: false
  }
});

interface ButtonProps 
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    appearance, 
    widthVariant, 
    center, 
    ejectPadding,
    className,
    children,
    icon,
    iconPosition = 'right',
    ...props 
  }, ref) => {
    return (
      <button 
        ref={ref} 
        className={buttonVariants({ 
          appearance, 
          widthVariant, 
          center, 
          ejectPadding, 
          className 
        })} 
        {...props}
      >
        <span className="flex items-baseline gap-5 gap-x-3 leading-none">
          {icon && iconPosition === 'left' && (
            <span className="w-[14.4px] h-[12.4px] items-center">{icon}</span>
          )}
          <span className="flex items-center">{children}</span>
          {icon && iconPosition === 'right' && (
            <span className="w-[14.4px] h-[12.4px] items-center">{icon}</span>
          )}
        </span>
      </button>
    );
  }
);

Button.displayName = "Button";